import {
  AnyObject,
  Field,
  FieldMap,
  FieldMapValue,
  ListField,
  isFieldMap,
  isInfoStage,
} from '@hb/shared'
import { Decorator } from 'final-form'
import createDecorator, { Calculation } from 'final-form-calculate'
import { get as nestedGet } from 'nested-property'
import { useMemo } from 'react'

const prefixField = (prefix: string, f: string) => `${prefix}.${f}`

const prefixCalculation = (calc: Calculation, prefix: string): Calculation => {
  const prefixedField = Array.isArray(calc.field)
    ? calc.field.map(f => prefixField(prefix, f))
    : prefixField(prefix, calc.field as string)

  if (typeof calc.updates === 'function') {
    const origFn = calc.updates
    return {
      ...calc,
      field: prefixedField,
      updates: (value: any, name: string, allValues: any, prevValues?: any) => {
        const scoped = origFn(value, name, allValues[prefix] ?? {}, prevValues?.[prefix])
        return Object.fromEntries(
          Object.entries(scoped).map(([k, v]) => [prefixField(prefix, k), v]),
        )
      },
    }
  }

  return {
    ...calc,
    field: prefixedField,
    updates: Object.fromEntries(
      Object.entries(calc.updates).map(([key, fn]) => [
        prefixField(prefix, key),
        (value: any, allValues: any, prevValues?: any) =>
          fn(value, nestedGet(allValues, prefix) ?? {}, nestedGet(prevValues, prefix)),
      ]),
    ),
  }
}

const collectConditionalCalculations = (fieldMap: FieldMap, prefix: string): Calculation[] => {
  const children = Object.entries(fieldMap.children)

  const conditionalEntries = children.filter(
    ([, child]) => !isInfoStage(child) && typeof child.condition === 'function',
  )

  if (conditionalEntries.length === 0) return []

  const watchedPaths = children
    .filter(([, child]) => !isInfoStage(child) && !isFieldMap(child))
    .map(([key]) => (prefix ? `${prefix}.${key}` : key))

  if (watchedPaths.length === 0) return []

  return [
    {
      field: watchedPaths,
      updates: (_value: any, _name: string, allValues: any): Record<string, undefined> => {
        const scopedValues: FieldMapValue = prefix
          ? (nestedGet(allValues, prefix) ?? {})
          : allValues

        const result: Record<string, undefined> = {}
        for (const [key, child] of conditionalEntries) {
          const { condition } = child
          if (condition && !condition(scopedValues)) {
            result[prefix ? `${prefix}.${key}` : key] = undefined
          }
        }
        return result
      },
    },
  ]
}

const collectCalculations = (
  fieldMap: FieldMap,
  prefix = '',
  parentCalculateOnConditional?: boolean,
): Calculation[] => {
  const own: Calculation[] = fieldMap.calculate
    ? prefix
      ? fieldMap.calculate.map(c => prefixCalculation(c, prefix))
      : fieldMap.calculate
    : []

  const shouldCalculateOnConditional =
    parentCalculateOnConditional || fieldMap.calculateOnConditional

  const conditional: Calculation[] = shouldCalculateOnConditional
    ? collectConditionalCalculations(fieldMap, prefix)
    : []

  const nested = Object.entries(fieldMap.children).flatMap(([key, child]) => {
    if (!isInfoStage(child) && isFieldMap(child)) {
      return collectCalculations(
        child,
        prefix ? `${prefix}.${key}` : key,
        shouldCalculateOnConditional,
      )
    }
    return []
  })

  return [...own, ...conditional, ...nested]
}

export const useCalculate = (field: Field | FieldMap | ListField): Calculation[] =>
  useMemo(() => {
    if (!isFieldMap(field)) return []
    return collectCalculations(field)
  }, [field])

export const useDecorators = <T extends AnyObject>(field: Field | FieldMap | ListField) => {
  const calculations = useCalculate(field)
  return useMemo(
    () =>
      calculations.length ? ([createDecorator(...calculations)] as Decorator<T>[]) : undefined,
    [calculations],
  )
}
